import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function Dialog({
  children,
  footer,
  onClose,
  open,
  role = 'dialog',
  title,
}) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    const frame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus?.();
    };
  }, [onClose, open]);

  if (!open) return null;

  const trapFocus = event => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) || []);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const dialog = (
    <div className="ui-dialog-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        aria-labelledby={title ? titleId : undefined}
        aria-modal="true"
        className="ui-dialog"
        onKeyDown={trapFocus}
        onMouseDown={event => event.stopPropagation()}
        role={role}
        tabIndex={-1}
      >
        <header className="ui-dialog__header">
          {title ? <h2 id={titleId} className="ui-dialog__title">{title}</h2> : <span />}
          <Button className="ui-dialog__close" variant="ghost" aria-label="Close dialog" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </Button>
        </header>
        <div className="ui-dialog__body">{children}</div>
        {footer && <footer className="ui-dialog__footer">{footer}</footer>}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
